import React, { useState } from 'react';
import { validateEmail } from '../../utilities/validators';
import { postForgetPassword } from '../../api/Api';
import RequestResultOverlay from '../../components/overlays/RequestResultOverlay';
import { Link, useHistory } from 'react-router-dom';
import { useLoadingContext } from '../../contexts/LoadingContext';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [displayOverlay, setDisplayOverlay] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const disabled = !validateEmail(email);
  const history = useHistory();

  const { setLoading } = useLoadingContext();

  const sendRequest = async (e) => {
    e.preventDefault();
    setIsFormSubmitted(true);
    setErrorMsg('');
    setLoading(true);
    
    try {
      const response = await postForgetPassword({ email });
      
      if (response.ok) {
        setDisplayOverlay(true);
      } else {
        setErrorMsg('Algo salió mal. Por favor intente nuevamente o comuníquese con el administrador');
      }
    } catch (error) {
      console.error('Error en recuperación de contraseña:', error);
      setErrorMsg('Error de conexión. Por favor verifique su conexión a internet e intente nuevamente');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errorMsg) setErrorMsg(''); // Limpiar error al escribir
  };

  return (
    <div className="min-height-100vh has-background-light">
      {/* Hero Section con Gradiente */}
      <section className="hero is-medium">
        <div 
          className="hero-body"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Elementos decorativos de fondo */}
          <div 
            style={{
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              transform: 'rotate(45deg)'
            }}
          ></div>
          <div 
            style={{
              position: 'absolute',
              bottom: '-30%',
              left: '-10%',
              width: '300px',
              height: '300px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '50%'
            }}
          ></div>

          <div className="container">
            <div className="columns is-centered">
              <div className="column is-5-desktop is-6-tablet is-10-mobile">
                {/* Card Principal */}
                <div 
                  className="box" 
                  style={{
                    borderRadius: '16px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {/* Header de la Card */}
                  <div className="has-text-centered mb-5">
                    <span 
                      className="icon is-large mb-3"
                      style={{
                        fontSize: '3rem',
                        color: '#667eea'
                      }}
                    >
                      <i className="fas fa-key"></i>
                    </span>
                    <h1 className="title is-4 has-text-grey-dark mb-2">
                      ¿Olvidaste tu contraseña?
                    </h1>
                    <p className="subtitle is-6 has-text-grey">
                      No te preocupes, te ayudaremos a recuperar el acceso a tu cuenta
                    </p>
                  </div>

                  {/* Formulario */}
                  <form onSubmit={sendRequest}>
                    <div className="field">
                      <label className="label has-text-weight-semibold has-text-grey-dark">
                        <span className="icon-text">
                          <span className="icon is-small mr-2">
                            <i className="fas fa-envelope"></i>
                          </span>
                          <span>Correo Electrónico</span>
                        </span>
                      </label>
                      <div className="control has-icons-left has-icons-right">
                        <input 
                          className={`input is-medium ${
                            isFormSubmitted 
                              ? validateEmail(email) 
                                ? 'is-success' 
                                : 'is-danger'
                              : ''
                          }`}
                          type="email" 
                          placeholder="ejemplo@correo.com"
                          value={email}
                          onChange={handleEmailChange}
                          autoFocus
                          style={{
                            borderRadius: '8px',
                            transition: 'all 0.3s ease'
                          }}
                        />
                        <span className="icon is-small is-left">
                          <i className="fas fa-envelope"></i>
                        </span>
                        {isFormSubmitted && validateEmail(email) && (
                          <span className="icon is-small is-right has-text-success">
                            <i className="fas fa-check"></i>
                          </span>
                        )}
                      </div>
                      {isFormSubmitted && !validateEmail(email) && (
                        <p className="help is-danger">
                          <span className="icon is-small mr-1">
                            <i className="fas fa-exclamation-triangle"></i>
                          </span>
                          Por favor ingresa un correo electrónico válido
                        </p>
                      )}
                    </div>

                    {/* Mensaje de Error */}
                    {errorMsg && (
                      <div className="notification is-danger is-light">
                        <span className="icon-text">
                          <span className="icon mr-2">
                            <i className="fas fa-exclamation-circle"></i>
                          </span>
                          <span>{errorMsg}</span>
                        </span>
                      </div>
                    )}

                    {/* Botón de Envío */}
                    <button
                      type="submit"
                      className={`button is-primary is-medium is-fullwidth has-text-weight-semibold ${
                        disabled ? 'is-loading' : ''
                      }`}
                      disabled={disabled}
                      style={{
                        borderRadius: '8px',
                        background: disabled 
                          ? '#dbdbdb' 
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        transition: 'all 0.3s ease',
                        transform: disabled ? 'none' : 'translateY(0)',
                        boxShadow: disabled 
                          ? 'none' 
                          : '0 4px 12px rgba(102, 126, 234, 0.4)'
                      }}
                      onMouseEnter={(e) => {
                        if (!disabled) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!disabled) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                        }
                      }}
                    >
                      <span className="icon mr-2">
                        <i className="fas fa-paper-plane"></i>
                      </span>
                      <span>Enviar Instrucciones</span>
                    </button>
                  </form>

                  {/* Información Adicional */}
                  <div className="has-text-centered mt-5 pt-4" style={{ borderTop: '1px solid #f5f5f5' }}>
                    <p className="is-size-7 has-text-grey mb-3">
                      <span className="icon is-small mr-1">
                        <i className="fas fa-info-circle"></i>
                      </span>
                      Te enviaremos un correo con las instrucciones para restablecer tu contraseña
                    </p>
                    
                    {/* Botón Cancelar */}
                    <Link 
                      to="/" 
                      replace 
                      className="button is-text is-medium"
                      style={{
                        textDecoration: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <span className="icon mr-2">
                        <i className="fas fa-arrow-left"></i>
                      </span>
                      <span>Volver al Inicio de Sesión</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sección de Ayuda */}
      <section className="section">
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-8">
              <div className="box has-background-white-bis">
                <h3 className="title is-5 has-text-centered mb-4">
                  <span className="icon mr-2 has-text-primary">
                    <i className="fas fa-question-circle"></i>
                  </span>
                  ¿Necesitas más ayuda?
                </h3>
                <div className="columns">
                  <div className="column">
                    <div className="content">
                      <h4 className="subtitle is-6">
                        <span className="icon mr-2">
                          <i className="fas fa-shield-alt"></i>
                        </span>
                        Seguridad
                      </h4>
                      <p className="is-size-7">
                        Nunca compartimos tu información personal. El proceso de recuperación es completamente seguro.
                      </p>
                    </div>
                  </div>
                  <div className="column">
                    <div className="content">
                      <h4 className="subtitle is-6">
                        <span className="icon mr-2">
                          <i className="fas fa-clock"></i>
                        </span>
                        Tiempo de Espera
                      </h4>
                      <p className="is-size-7">
                        El correo puede tardar hasta 5 minutos en llegar. Revisa también tu carpeta de spam.
                      </p>
                    </div>
                  </div>
                  <div className="column">
                    <div className="content">
                      <h4 className="subtitle is-6">
                        <span className="icon mr-2">
                          <i className="fas fa-life-ring"></i>
                        </span>
                        Soporte
                      </h4>
                      <p className="is-size-7">
                        Si no recibes el correo, contacta al administrador del sistema para obtener ayuda.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de Resultado */}
      <RequestResultOverlay
        title="Correo Enviado"
        isActive={displayOverlay}
        description="Te hemos enviado un correo electrónico con las instrucciones para restablecer tu contraseña. Si no lo encuentras, revisa tu carpeta de spam."
        onPressConfirm={() => history.push('/')}
        buttonTitle="Entendido"
      />
    </div>
  );
};

export default ForgotPasswordPage;